import type { SettingsConstants } from '@/contexts/SettingsContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import type { SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import { EyeOff } from 'lucide-react';
import type { ReactNode } from 'react';

export type NavVisibilityKey = keyof SettingsConstants['navigationVisibility'];

export function useNavigationVisibilityUi() {
    const { settings } = useSettings();
    const page = usePage<SharedData>();
    const { t } = useTranslations();

    const roles = page.props.auth?.roles ?? [];
    const userRole = page.props.auth?.user?.role;
    const isAdministrator = userRole === 'Administrator' || roles.includes('Administrator');

    const isNavKeyVisible = (key: NavVisibilityKey): boolean => (settings.navigationVisibility?.[key] ?? true) === true;

    const shouldShowForNavKey = (key: NavVisibilityKey | undefined): boolean => {
        if (!key) {
            return true;
        }

        return isNavKeyVisible(key) || isAdministrator;
    };

    const isAdminOnlyHiddenNavKey = (key: NavVisibilityKey | undefined): boolean => Boolean(key && isAdministrator && !isNavKeyVisible(key));

    const tabDisplayLabel = (label: string, key: NavVisibilityKey | undefined, count?: number): ReactNode => {
        const suffix = count !== undefined ? ` (${count})` : '';
        const hiddenHint = t('navigation.hidden_from_main_nav', 'Hidden from main navigation');

        if (!isAdminOnlyHiddenNavKey(key)) {
            return (
                <>
                    {label}
                    {suffix}
                </>
            );
        }

        return (
            <span className="text-muted-foreground flex items-center gap-1.5" title={hiddenHint}>
                <EyeOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                    {label}
                    {suffix}
                </span>
            </span>
        );
    };

    return {
        isAdministrator,
        isNavKeyVisible,
        shouldShowForNavKey,
        isAdminOnlyHiddenNavKey,
        tabDisplayLabel,
    };
}
